import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormProvider, useForm } from "react-hook-form";
import PageHeader from "../../Layout/Header/PageHeader";
import PageHeaderButton from "../../Layout/Header/PageHeaderButton";
import { MdmTabs } from "../../Layout/Tabs/MdmTabs";
import PageHeaderIconButton from "../../Layout/Header/PageHeaderIconButton";
import { Contact } from "../Contact";
import { contactSchema } from "./contactSchema";
import { ContactForm } from "./ContactForm";
import { contactDefaultValues } from "./contactDefaultValues";
import { confirm } from "../../Layout/Dialog/ConfirmDialog";
import { INFORMATION } from "../../Layout/Tabs/MdmTabPanelConstants";
import { isFormSubmitForbidden } from "../../Forms/isFormSubmitForbidden";
import { resetFormStateAndKeepFormValues } from "../../Forms/resetFormStateAndKeepFormValues";
import { useIsUserInRoles } from "../../Auth/useIsUserInRoles";
import { AuthorizationPolicies } from "../../Auth/AuthorizationPolicy";

type Props = {
  title: string;
  contact: Contact | undefined;
  onSave: (contact: Contact) => void;
  onDelete?: (contact: Contact) => void;
};

export default function ContactFormTabs({ title, contact, onSave, onDelete }: Props) {
  const isUserAdminNetworkRiskLaunchRole = useIsUserInRoles(
    AuthorizationPolicies.RequireAdminNetworkRiskLaunchRoles
  );

  const methods = useForm<Contact>({
    resolver: yupResolver(contactSchema),
    defaultValues: contactDefaultValues,
    mode: "all",
    disabled: !isUserAdminNetworkRiskLaunchRole,
  });

  useEffect(() => {
    methods.reset(contact);
    return () => methods.reset(contactDefaultValues);
  }, [contact, methods, methods.reset]);

  const onDeleteWithConfirmation = methods.handleSubmit(async (values: Contact) => {
    const isConfirmed = await confirm(
      `Do you really want to delete contact with Id ${contact?.Id}?`
    );
    if (!isConfirmed || !onDelete) {
      return;
    }
    await onDelete(values);
  });

  return (
    <>
      <PageHeader headerText={title}>
        {onDelete !== undefined && (
          <PageHeaderIconButton
            onClick={() => onDeleteWithConfirmation()}
            color="secondary"
            disabled={!isUserAdminNetworkRiskLaunchRole}
          >
            <DeleteIcon />
          </PageHeaderIconButton>
        )}
        <PageHeaderButton
          onClick={methods.handleSubmit((values: Contact) => {
            onSave(values);
            resetFormStateAndKeepFormValues(methods);
          })}
          disabled={isFormSubmitForbidden(methods.formState)}
        >
          Save
        </PageHeaderButton>
      </PageHeader>

      <FormProvider {...methods}>
        <MdmTabs
          tabs={[
            {
              index: 0,
              label: INFORMATION,
              content: <ContactForm />,
            },
          ]}
        />
      </FormProvider>
    </>
  );
}
